// src/pages/VMRulesMonitor.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import algoliasearch from 'algoliasearch';
import { getApiKey, getAppId } from '../services/sessionService';
import { trackVMRulesMonitor, trackExportCSV, trackError } from '../services/analyticsService';
import SectionBlock from '../components/SectionBlock';
import InfoBlock from '../components/InfoBlock';
import StyledButton from '../components/StyledButton';
import FullPageLoader from '../components/FullPageLoader';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import {
  Input,
  Select,
  Label,
  FormGroup,
  Hint,
  StatusBadge,
  StatusDot
} from '../components/FormElements';

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 5px 0 0 0;
  position: absolute;
  background-color: var(--paper-raised);
  border: 1.5px solid var(--line-strong);
  border-radius: var(--radius-md);
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
`;

const SuggestionItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: var(--gray-700);
  transition: background-color var(--transition-fast);

  &:hover {
    background-color: var(--primary-50);
    color: var(--primary-600);
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const RuleHeaderRow = styled.tr`
  cursor: pointer;
  background-color: var(--paper);

  &:hover {
    background-color: var(--accent-tint);
  }
`;

const Chevron = styled.span`
  display: inline-block;
  font-size: 10px;
  margin-right: 8px;
  transition: transform var(--transition-fast);
  transform: ${props => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const MatchCount = styled.span`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  color: var(--ink-faint);
  margin-left: 8px;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;

  th, td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid var(--line);
    font-size: 13px;
    color: var(--ink-soft);
    vertical-align: top;
  }

  th {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: var(--ink-faint);
    white-space: nowrap;
  }
`;

const RuleLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--accent-ink);
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ProductThumb = styled.img`
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  border: 1px solid var(--line);
  background-color: var(--paper);
  display: block;
`;

const parseValue = (raw) => {
  const trimmed = raw.trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
};

const extractPromotedObjectIDs = (rule) => {
  const promote = rule.consequence?.promote || [];
  const pins = [];
  promote.forEach((entry) => {
    if (entry.objectID) pins.push({ objectID: entry.objectID, position: entry.position });
    if (Array.isArray(entry.objectIDs)) {
      entry.objectIDs.forEach(objectID => pins.push({ objectID, position: entry.position }));
    }
  });
  return pins;
};

const extractCategoryIds = (rule) => {
  const conditions = rule.conditions || (rule.condition ? [rule.condition] : []);
  const ids = [];
  conditions.forEach((condition) => {
    const filters = condition.filters || '';
    const matches = filters.matchAll(/categoryIds"?\s*:\s*"([^"]+)"/g);
    for (const match of matches) {
      ids.push(match[1]);
    }
    if (condition.context) ids.push(condition.context);
  });
  return [...new Set(ids)];
};

const isQueryBasedRule = (rule) => {
  const conditions = rule.conditions || (rule.condition ? [rule.condition] : []);
  return conditions.some(c => typeof c.pattern === 'string' && c.pattern.trim() !== '');
};

const VMRulesMonitor = () => {
  const [indexName, setIndexName] = useState('');
  const [allIndexes, setAllIndexes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [distinctAttribute, setDistinctAttribute] = useState('');
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [filterPairs, setFilterPairs] = useState([{ attribute: '', value: '' }]);
  const [imageAttribute, setImageAttribute] = useState('');

  const [results, setResults] = useState(null);
  const [expandedRuleIds, setExpandedRuleIds] = useState(new Set());
  const [log, setLog] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const apiKey = getApiKey();
  const appId = getAppId();

  useEffect(() => {
    if (appId && apiKey) {
      const searchClient = algoliasearch(appId, apiKey);
      searchClient.listIndices().then(({ items }) => {
        const primaryIndexes = items.filter(item => !item.primary);
        const cleanedNames = primaryIndexes.map(item => item.name.trim());
        const uniqueNames = [...new Set(cleanedNames)];
        setAllIndexes(uniqueNames.sort());
      }).catch(err => {
        console.error("Failed to fetch index list for autocomplete:", err);
      });
    }
  }, [appId, apiKey]);

  useEffect(() => {
    if (indexName && allIndexes.length > 0) {
      const filtered = allIndexes.filter(name =>
        name.toLowerCase().includes(indexName.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [indexName, allIndexes]);

  const handleSuggestionClick = (name) => {
    setIndexName(name);
    setSuggestions([]);
    setIsInputFocused(false);
  };

  const handleAddFilterPair = () => {
    setFilterPairs([...filterPairs, { attribute: '', value: '' }]);
  };

  const handleRemoveFilterPair = (idxToRemove) => {
    setFilterPairs(filterPairs.filter((_, idx) => idx !== idxToRemove));
  };

  const handleFilterPairChange = (field, idx, value) => {
    const updated = [...filterPairs];
    updated[idx] = { ...updated[idx], [field]: value };
    setFilterPairs(updated);
  };

  const synchronizeIndexData = async () => {
    if (!indexName) {
      setError('Please provide an index name first.');
      return;
    }
    setIsLoading(true);
    setError('');
    setResults(null);
    setDistinctAttribute('');
    setAvailableAttributes([]);
    setImageAttribute('');
    let syncLog = `Syncing data for index '${indexName}'...\n`;
    setLog(syncLog);

    try {
      const client = algoliasearch(appId, apiKey);
      const index = client.initIndex(indexName);

      const settings = await index.getSettings();
      if (settings.attributeForDistinct) {
        setDistinctAttribute(settings.attributeForDistinct);
        syncLog += `✅ Distinct attribute found: ${settings.attributeForDistinct}\n`;
      } else {
        syncLog += `ℹ️ No distinct attribute defined in index settings. The distinct value column will be empty.\n`;
      }

      const response = await index.search('', { hitsPerPage: 10 });
      const attributeSet = new Set();
      response.hits.forEach(hit => {
        Object.keys(hit).forEach(key => attributeSet.add(key));
      });
      const sortedAttributes = [...attributeSet].sort((a, b) => a.localeCompare(b));
      setAvailableAttributes(sortedAttributes);
      syncLog += `✅ Found ${sortedAttributes.length} available attributes.\n`;
      setLog(syncLog);
    } catch (err) {
      setError('Error during synchronization: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const buildRuleUrl = (ruleObjectID) => (
    `https://dashboard.algolia.com/apps/${appId}/rules/${encodeURIComponent(indexName)}/visual-editor/edit/${encodeURIComponent(ruleObjectID)}`
  );

  const toggleRuleExpanded = (ruleObjectID) => {
    setExpandedRuleIds(prev => {
      const next = new Set(prev);
      if (next.has(ruleObjectID)) next.delete(ruleObjectID);
      else next.add(ruleObjectID);
      return next;
    });
  };

  const handleExpandAll = () => {
    setExpandedRuleIds(new Set(results.map(r => r.ruleObjectID)));
  };

  const handleCollapseAll = () => {
    setExpandedRuleIds(new Set());
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value).replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
    const needsQuotes = stringValue.includes(';') || stringValue.includes('"');
    return needsQuotes ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
  };

  const handleExportCsv = () => {
    if (!results || results.length === 0) return;

    const headers = [
      'ruleObjectID',
      'ruleDescription',
      'ruleUrl',
      'categoryIds',
      'objectID',
      distinctAttribute || 'distinctValue',
      'pinnedPosition',
    ];

    const rows = [headers.join(';')];
    let recordCount = 0;

    results.forEach(rule => {
      rule.matches.forEach(match => {
        const row = [
          rule.ruleObjectID,
          rule.ruleDescription,
          buildRuleUrl(rule.ruleObjectID),
          rule.categoryIds.join(', '),
          match.objectID,
          match.distinctValue,
          match.position,
        ].map(escapeCsvValue);
        rows.push(row.join(';'));
        recordCount += 1;
      });
    });

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `vm_rules_monitor_${indexName}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    trackExportCSV(indexName, recordCount, 'vm_rules_monitor');
  };

  const handleCheckRules = async () => {
    setError('');
    setLog('');
    setResults(null);

    if (!appId || !apiKey) {
      setError('Error: App ID and API Key are missing. Please add them in the "Credentials" section.');
      return;
    }
    if (!indexName) {
      setError('Please provide an index name.');
      return;
    }
    if (availableAttributes.length === 0) {
      setError('Please sync the index attributes first.');
      return;
    }

    const activeFilters = filterPairs.filter(f => f.attribute && f.value !== '');
    if (activeFilters.length === 0) {
      setError('Please add at least one attribute filter value (e.g. online: false).');
      return;
    }

    setIsLoading(true);

    try {
      const client = algoliasearch(appId, apiKey);
      const index = client.initIndex(indexName);

      const rules = [];
      await index.browseRules({ batch: batch => rules.push(...batch) });

      const eligibleRules = rules.filter(rule => rule.enabled !== false && !isQueryBasedRule(rule));

      const rulesWithPromotes = eligibleRules
        .map(rule => ({ rule, pins: extractPromotedObjectIDs(rule) }))
        .filter(entry => entry.pins.length > 0);

      const allObjectIDs = [...new Set(rulesWithPromotes.flatMap(entry => entry.pins.map(p => p.objectID)))];

      if (allObjectIDs.length === 0) {
        setResults([]);
        setExpandedRuleIds(new Set());
        setLog(`Scanned ${rules.length} rule(s) (${eligibleRules.length} eligible, excluding disabled and query-based rules). None of them pin any product in this index.`);
        trackVMRulesMonitor(indexName, rules.length, 0, 0);
        return;
      }

      const retrievableAttributes = [
        'objectID',
        ...(distinctAttribute ? [distinctAttribute] : []),
        ...(imageAttribute ? [imageAttribute] : []),
        ...activeFilters.map(f => f.attribute),
      ];
      const uniqueAttributes = [...new Set(retrievableAttributes)];

      const objectsByID = {};
      const CHUNK_SIZE = 1000;
      for (let i = 0; i < allObjectIDs.length; i += CHUNK_SIZE) {
        const chunk = allObjectIDs.slice(i, i + CHUNK_SIZE);
        const { results: chunkResults } = await index.getObjects(chunk, { attributesToRetrieve: uniqueAttributes });
        chunkResults.forEach((obj, idx) => {
          if (obj) objectsByID[chunk[idx]] = obj;
        });
      }

      const matchesFilters = (obj) => activeFilters.every(({ attribute, value }) => (
        obj[attribute] === parseValue(value)
      ));

      const flaggedRules = rulesWithPromotes
        .map(({ rule, pins }) => {
          const matches = pins
            .map(pin => ({ pin, obj: objectsByID[pin.objectID] }))
            .filter(({ obj }) => obj && matchesFilters(obj))
            .map(({ pin, obj }) => ({
              objectID: obj.objectID,
              distinctValue: distinctAttribute ? obj[distinctAttribute] : undefined,
              imageUrl: imageAttribute ? obj[imageAttribute] : undefined,
              position: Number.isInteger(pin.position) ? pin.position + 1 : undefined,
            }));
          return {
            ruleObjectID: rule.objectID,
            ruleDescription: rule.description || '',
            categoryIds: extractCategoryIds(rule),
            matches,
          };
        })
        .filter(entry => entry.matches.length > 0);

      const totalMatches = flaggedRules.reduce((sum, r) => sum + r.matches.length, 0);
      setResults(flaggedRules);
      setExpandedRuleIds(new Set(flaggedRules.map(r => r.ruleObjectID)));
      setLog(`Scanned ${rules.length} rule(s) (${eligibleRules.length} eligible, excluding disabled and query-based rules), ${rulesWithPromotes.length} of which pin products. Found ${flaggedRules.length} rule(s) pinning ${totalMatches} product(s) matching your filter.`);
      trackVMRulesMonitor(indexName, rules.length, flaggedRules.length, totalMatches);
    } catch (err) {
      trackError('vm_rules_monitor', err.message, 'vm_rules_monitor_error');
      setError(`An error occurred: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <FullPageLoader isLoading={isLoading} />

      <PageHeader
        title="VM Rules Monitor"
        subtitle="Find pinned products in Visual Merchandising rules that don't match your online filter"
      >
        {appId && apiKey && (
          <StatusBadge className="success">
            <StatusDot />
            Connected
          </StatusBadge>
        )}
      </PageHeader>

      <InfoBlock title="How this works" icon="alert-triangle">
        Algolia's <code>distinct</code> setting collapses your SKUs into a single result per distinct value (e.g. one SMC per style/color). If a merchandiser pins a specific SKU in a VM rule and that SKU later becomes filtered out (e.g. <code>online:false</code>), it silently disappears from the site.
        <br/><br/>
        Sync an index, define the attribute value(s) that should flag a product, and this tool scans every rule for pinned objectIDs matching that filter.
      </InfoBlock>

      <SectionBlock title="Index Settings">
        <FormGroup>
          <Label>Index Name</Label>
          <AutocompleteContainer>
            <Input
              type="text"
              value={indexName}
              onChange={(e) => setIndexName(e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setTimeout(() => setIsInputFocused(false), 200)}
              placeholder="Search for a primary index..."
            />
            {isInputFocused && suggestions.length > 0 && (
              <SuggestionsList>
                {suggestions.slice(0, 10).map(suggestion => (
                  <SuggestionItem key={suggestion} onClick={() => handleSuggestionClick(suggestion)}>
                    {suggestion}
                  </SuggestionItem>
                ))}
              </SuggestionsList>
            )}
          </AutocompleteContainer>
        </FormGroup>

        <StyledButton onClick={synchronizeIndexData} label="Sync Attributes" icon="refresh" variant="secondary" />

        {distinctAttribute && (
          <Hint className="success" style={{ marginTop: '12px' }}>
            Distinct Attribute detected: <strong>{distinctAttribute}</strong>
          </Hint>
        )}
      </SectionBlock>

      {availableAttributes.length > 0 && (
        <SectionBlock title="Display Options">
          <FormGroup style={{ marginBottom: 0 }}>
            <Label>Image Attribute (optional)</Label>
            <Select
              value={imageAttribute}
              onChange={(e) => setImageAttribute(e.target.value)}
            >
              <option value="">-- No image column --</option>
              {availableAttributes.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </Select>
            <Hint>Pick the attribute that holds the product image URL to show a thumbnail in the results table.</Hint>
          </FormGroup>
        </SectionBlock>
      )}

      {availableAttributes.length > 0 && (
        <SectionBlock title="Filter Values (a pinned product is flagged when it matches ALL of these)">
          {filterPairs.map((pair, idx) => (
            <FilterRow key={idx}>
              <Select
                value={pair.attribute}
                onChange={(e) => handleFilterPairChange('attribute', idx, e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">-- Attribute --</option>
                {availableAttributes.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Select>
              <Input
                type="text"
                placeholder="Value (e.g. false)"
                value={pair.value}
                onChange={(e) => handleFilterPairChange('value', idx, e.target.value)}
                style={{ flex: 1 }}
              />
              {idx === filterPairs.length - 1 ? (
                <StyledButton onClick={handleAddFilterPair} label="Add" icon="plus" variant="success" size="sm" />
              ) : (
                <StyledButton onClick={() => handleRemoveFilterPair(idx)} label="Remove" icon="x" variant="danger" size="sm" />
              )}
            </FilterRow>
          ))}
        </SectionBlock>
      )}

      <SectionBlock title="Actions">
        <StyledButton onClick={handleCheckRules} label="Check VM Rules" icon="search" variant="primary" size="lg" />
      </SectionBlock>

      {results && results.length > 0 && (
        <SectionBlock title="Flagged VM Rules" style={{ maxWidth: '1100px' }}>
          <ButtonGroup>
            <StyledButton onClick={handleExpandAll} label="Expand All" variant="secondary" size="sm" />
            <StyledButton onClick={handleCollapseAll} label="Collapse All" variant="secondary" size="sm" />
            <StyledButton onClick={handleExportCsv} label="Export CSV" icon="folder" variant="secondary" size="sm" />
          </ButtonGroup>
          <TableWrapper>
            <ResultsTable>
              <thead>
                <tr>
                  {imageAttribute && <th>Image</th>}
                  <th>objectID</th>
                  <th>{distinctAttribute || 'Distinct value'}</th>
                  <th>Pinned Position</th>
                </tr>
              </thead>
              <tbody>
                {results.map((rule) => {
                  const isOpen = expandedRuleIds.has(rule.ruleObjectID);
                  const colSpan = 3 + (imageAttribute ? 1 : 0);
                  return (
                    <React.Fragment key={rule.ruleObjectID}>
                      <RuleHeaderRow onClick={() => toggleRuleExpanded(rule.ruleObjectID)}>
                        <td colSpan={colSpan}>
                          <Chevron $isOpen={isOpen}>▾</Chevron>
                          <RuleLink
                            href={buildRuleUrl(rule.ruleObjectID)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Icon name="external-link" size={14} />
                            {rule.ruleDescription || rule.ruleObjectID}
                          </RuleLink>
                          <MatchCount>
                            {rule.categoryIds.length > 0 ? `Category ID: ${rule.categoryIds.join(', ')} · ` : ''}
                            {rule.matches.length} product(s) flagged
                          </MatchCount>
                        </td>
                      </RuleHeaderRow>
                      {isOpen && rule.matches.map((match) => (
                        <tr key={`${rule.ruleObjectID}-${match.objectID}`}>
                          {imageAttribute && (
                            <td>
                              {match.imageUrl ? (
                                <ProductThumb src={match.imageUrl} alt={match.objectID} />
                              ) : '—'}
                            </td>
                          )}
                          <td>{match.objectID}</td>
                          <td>{match.distinctValue ?? '—'}</td>
                          <td>{match.position ?? '—'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </ResultsTable>
          </TableWrapper>
        </SectionBlock>
      )}

      {results && results.length === 0 && !error && (
        <Hint className="success" style={{ marginTop: '20px' }}>No pinned product currently matches your filter.</Hint>
      )}

      {error && <Hint className="error" style={{ marginTop: '20px' }}>{error}</Hint>}
      {log && <Hint className="success" style={{ marginTop: '20px', whiteSpace: 'pre-line' }}>{log}</Hint>}
    </div>
  );
};

export default VMRulesMonitor;
